// frontend/src/components/booking-management/BookingFilters.js

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { Search, Filter } from 'lucide-react';
import { isAdmin } from '../../utils/roleUtils';
import { formatDateISO, parseLocalDate } from '../../utils/dateTimeFormatter';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import enUS from 'date-fns/locale/en-US';
import referenceService from '../../services/referenceService';
import logger from '../../utils/logger';

/**
 * Role-adaptive filters for BookingView component
 * - Users: Basic filters (status, date range, time range)
 * - Admins: Advanced filters (route, status, date range, time range, search)
 * - Hidden if total bookings < filter threshold
 */
const BookingFilters = ({
  userRole,
  filters,
  onFilterChange,
  onInitialized,
  preferredDateFormat = 'MM/DD/YYYY',
  userLanguage = 'en',
  routes = [],
  showSearch = false,
  bookingWindowDays = 7
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const isAdminUser = isAdmin(userRole);
  const [datepickerLocale, setDatepickerLocale] = useState({ key: 'booking-en', locale: enUS });

  useEffect(() => {
    let cancelled = false;

    const loadDateLocale = async () => {
      const lang = (userLanguage || 'en').toLowerCase();

      // Resolve language code to locale code via the languages table
      let localeCode;
      try {
        localeCode = await referenceService.getLocaleForLanguage(lang);
      } catch {
        localeCode = 'en-US';
      }
      const normalizedLocaleCode = typeof localeCode === 'string' && localeCode.trim()
        ? localeCode.trim()
        : 'en-US';

      // Dynamically import the date-fns locale
      // date-fns uses directory names like "en-US", "zh-CN", "fr", "fr-CA"
      const candidates = [
        normalizedLocaleCode,                          // e.g. "fr-FR"
        normalizedLocaleCode.split('-')[0],            // e.g. "fr"
      ];

      let loadedLocale = null;
      for (const candidate of candidates) {
        try {
          const mod = await import(/* @vite-ignore */ `date-fns/locale/${candidate}`);
          loadedLocale = mod.default || mod;
          break;
        } catch {
          // locale not available in date-fns, try next candidate
        }
      }

      if (cancelled) return;

      const locale = loadedLocale || enUS;
      const key = `booking-${lang}`;
      setDatepickerLocale({ key, locale });
      registerLocale(key, locale);
    };

    loadDateLocale();
    return () => { cancelled = true; };
  }, [userLanguage]);

  const dateFormatForDisplay = useMemo(() => {
    const formatMap = {
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd'
    };
    return formatMap[preferredDateFormat] || 'MM/dd/yyyy';
  }, [preferredDateFormat]);

  const parseDateValue = useCallback((value) => {
    if (!value) return null;
    try {
      return parseLocalDate(value);
    } catch (error) {
      logger.warn('[BookingFilters] Failed to parse date value', error);
      return null;
    }
  }, []);

  // Local state for preset selections - Initialize with smart defaults
  const [datePreset, setDatePreset] = useState('recentAndUpcoming'); // Smart default: Last 2 days + Next 7 days
  const [timePreset, setTimePreset] = useState('all');

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Track if preset effects should skip (to avoid overriding smart defaults)
  const skipDatePresetEffect = useRef(true);
  const skipTimePresetEffect = useRef(true);

  // Track if filters are expanded/collapsed - collapsed by default
  const [isExpanded, setIsExpanded] = useState(false);
  // Track if user has interacted with filters (to avoid auto-expand on initial smart defaults)
  const userHasInteracted = useRef(false);

  // Determine if any filters beyond defaults are active
  const hasActiveFilters = Boolean(
    (filters.status && filters.status !== 'all') ||
    filters.routeId ||
    filters.startDate ||
    filters.endDate ||
    filters.startTime ||
    filters.endTime ||
    filters.search ||
    datePreset !== 'all' ||
    timePreset !== 'all'
  );

  // Auto-expand only if user has manually changed filters (not on initial smart defaults)
  useEffect(() => {
    if (hasActiveFilters && userHasInteracted.current) {
      setIsExpanded(true);
    }
  }, [hasActiveFilters]);

  const updateFilters = useCallback((updates) => {
    onFilterChange((prevFilters) => {
      if (typeof updates === 'function') {
        return updates(prevFilters);
      }
      return {
        ...prevFilters,
        ...updates
      };
    });
  }, [onFilterChange]);

  const handleChange = (field, value) => {
    userHasInteracted.current = true; // Mark user interaction for auto-expand
    updateFilters({ [field]: value });
  };

  // Initialize smart defaults on mount - set ALL defaults at once
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      // Calculate date range for smart default
      const today = new Date();
      const toISO = (date) => formatDateISO(date);

      // Last 2 days + Next 7 days
      const last2 = new Date(today);
      last2.setDate(today.getDate() - 2);
      const bookingWindow = new Date(today);
      bookingWindow.setDate(today.getDate() + bookingWindowDays);

      // Set ALL smart defaults at once
      updateFilters((prev) => ({
        ...prev,
        status: prev.status || 'pending,confirmed,waitlist', // Use existing or set default
        startDate: toISO(last2),
        endDate: toISO(bookingWindow)
      }));

      // Signal that initialization is complete
      if (onInitialized) {
        onInitialized();
      }
    }
  }, [bookingWindowDays, onInitialized, updateFilters]);

  // Calculate date range based on preset selection
  useEffect(() => {
    // Skip on initial mount to avoid overriding smart defaults
    if (skipDatePresetEffect.current) {
      skipDatePresetEffect.current = false;
      return;
    }

    if (datePreset === 'custom') return; // Don't auto-calculate for custom

    const today = new Date();
    const toISO = (date) => formatDateISO(date);

    let startDate = '';
    let endDate = '';

    switch (datePreset) {
      case 'recentAndUpcoming': {
        // Smart default: Last 2 days + bookingWindowDays
        const last1 = new Date(today);
        last1.setDate(today.getDate() - 1);
        startDate = toISO(last1);
        const bookingWindow = new Date(today);
        bookingWindow.setDate(today.getDate() + bookingWindowDays);
        endDate = toISO(bookingWindow);
        break;
      }
      case 'today':
        startDate = toISO(today);
        endDate = toISO(today);
        break;
      case 'next2days': {
        startDate = toISO(today);
        const next2 = new Date(today);
        next2.setDate(today.getDate() + 2);
        endDate = toISO(next2);
        break;
      }
      case 'next7days': {
        startDate = toISO(today);
        const next7days = new Date(today);
        next7days.setDate(today.getDate() + 7);
        endDate = toISO(next7days);
        break;
      }
      case 'last2days': {
        const last2days = new Date(today);
        last2days.setDate(today.getDate() - 2);
        startDate = toISO(last2days);
        endDate = toISO(today);
        break;
      }
      case 'last7days': {
        const last7days = new Date(today);
        last7days.setDate(today.getDate() - 7);
        startDate = toISO(last7days);
        endDate = toISO(today);
        break;
      }
      case 'all':
      default:
        startDate = '';
        endDate = '';
        break;
    }

    updateFilters((prev) => ({
      ...prev,
      startDate,
      endDate
    }));
  }, [bookingWindowDays, datePreset, updateFilters]);

  // Calculate time range based on preset selection
  useEffect(() => {
    // Skip on initial mount to avoid overriding smart defaults
    if (skipTimePresetEffect.current) {
      skipTimePresetEffect.current = false;
      return;
    }

    if (timePreset === 'custom') return; // Don't auto-calculate for custom

    let startTime = '';
    let endTime = '';

    switch (timePreset) {
      case 'lateNight':
        startTime = '00:00';
        endTime = '06:00';
        break;
      case 'morning':
        startTime = '06:00';
        endTime = '12:00';
        break;
      case 'afternoon':
        startTime = '12:00';
        endTime = '18:00';
        break;
      case 'evening':
        startTime = '18:00';
        endTime = '23:59';
        break;
      case 'all':
      default:
        startTime = '';
        endTime = '';
        break;
    }

    updateFilters((prev) => ({
      ...prev,
      startTime,
      endTime
    }));
  }, [timePreset, updateFilters]);

  // ALWAYS show filter toggle - with smart defaults, there's no performance issue
  // Users need access to filters to change date ranges even with few bookings
  return (
    <div className={cn('mb-6 rounded-lg border', background.secondary, 'border-gray-200 dark:border-gray-700')}>
      {/* Filter Toggle Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-t-lg',
          isExpanded && 'border-b border-gray-200 dark:border-gray-700'
        )}
      >
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-500" />
          <h3 className={cn('text-lg font-semibold', text.primary)}>
            {t('filters')}
          </h3>
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-blue-500 text-white">
              {t('active')}
            </span>
          )}
        </div>
        <svg
          className={cn(
            'w-5 h-5 transition-transform',
            text.secondary,
            isExpanded && 'transform rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Filter Content - Collapsible */}
      {isExpanded && (
        <div className="p-4">

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Filter - Available for both users and admins */}
        <div>
          <label className={cn('block text-sm font-medium mb-1', text.primary)}>
            {t('status')}
          </label>
          <select
            value={filters.status || 'all'}
            onChange={(e) => handleChange('status', e.target.value)}
            className={cn(
              'w-full p-2 border rounded-md',
              background.primary,
              text.primary,
              'border-gray-200 dark:border-gray-700',
              'focus:ring-2 focus:ring-blue-500'
            )}
          >
            <option value="all">{t('allStatus')}</option>
            <option value="pending,confirmed,waitlist">{t('activeBookings')} ({t('bookingStatus.pending')}, {t('bookingStatus.confirmed')}, {t('bookingStatus.waitlist')})</option>
            <option value="pending">{t('bookingStatus.pending')}</option>
            <option value="confirmed">{t('bookingStatus.confirmed')}</option>
            <option value="waitlist">{t('bookingStatus.waitlist')}</option>
            <option value="completed">{t('bookingStatus.completed')}</option>
            <option value="canceled">{t('bookingStatus.canceled')}</option>
            <option value="no_show">{t('bookingStatus.no_show')}</option>
          </select>
        </div>

        {/* Route Filter - Admin only */}
        {isAdminUser && routes.length > 0 && (
          <div>
            <label className={cn('block text-sm font-medium mb-1', text.primary)}>
              {t('route')}
            </label>
            <select
              value={filters.routeId || ''}
              onChange={(e) => handleChange('routeId', e.target.value)}
              className={cn(
                'w-full p-2 border rounded-md',
                background.primary,
                text.primary,
                'border-gray-200 dark:border-gray-700',
                'focus:ring-2 focus:ring-blue-500'
              )}
            >
              <option value="">{t('allRoutes')}</option>
              {routes.map(route => (
                <option key={route.id} value={route.id}>
                  {route.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Date Preset Filter */}
        <div>
          <label className={cn('block text-sm font-medium mb-1', text.primary)}>
            {t('dateFilter')}
          </label>
          <select
            value={datePreset}
            onChange={(e) => {
              userHasInteracted.current = true;
              setDatePreset(e.target.value);
            }}
            className={cn(
              'w-full p-2 border rounded-md',
              background.primary,
              text.primary,
              'border-gray-200 dark:border-gray-700',
              'focus:ring-2 focus:ring-blue-500'
            )}
          >
            <option value="all">{t('allDates')}</option>
            <option value="recentAndUpcoming">{t('last1Day')} + {bookingWindowDays} {t('days')}({t('bookingWindow')})</option>
            <option value="today">{t('today')}</option>
            <option value="next2days">{t('next2Days')}</option>
            <option value="next7days">{t('next7Days')}</option>
            <option value="last2days">{t('last2Days')}</option>
            <option value="last7days">{t('last7Days')}</option>
            <option value="custom">{t('customDateRange')}</option>
          </select>
        </div>

        {/* Time Preset Filter */}
        <div>
          <label className={cn('block text-sm font-medium mb-1', text.primary)}>
            {t('timeFilter')}
          </label>
          <select
            value={timePreset}
            onChange={(e) => {
              userHasInteracted.current = true;
              setTimePreset(e.target.value);
            }}
            className={cn(
              'w-full p-2 border rounded-md',
              background.primary,
              text.primary,
              'border-gray-200 dark:border-gray-700',
              'focus:ring-2 focus:ring-blue-500'
            )}
          >
            <option value="all">{t('allTimes')}</option>
            <option value="lateNight">{t('lateNight')} (00:00 - 06:00)</option>
            <option value="morning">{t('morning')} (06:00 - 12:00)</option>
            <option value="afternoon">{t('afternoon')} (12:00 - 18:00)</option>
            <option value="evening">{t('evening')} (18:00 - 00:00)</option>
            <option value="custom">{t('customTimeRange')}</option>
          </select>
        </div>

        {/* Search Filter - Admin only, if enabled */}
        {isAdminUser && showSearch && (
          <div className="md:col-span-2">
            <label className={cn('block text-sm font-medium mb-1', text.primary)}>
              {t('search')}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleChange('search', e.target.value)}
                placeholder={t('searchByNameEmailOrPhone')}
                className={cn(
                  'w-full pl-10 p-2 border rounded-md',
                  background.primary,
                  text.primary,
                  'border-gray-200 dark:border-gray-700',
                  'focus:ring-2 focus:ring-blue-500'
                )}
              />
            </div>
          </div>
        )}
      </div>

      {/* Custom Date Range Inputs - Only show when custom is selected */}
      {datePreset === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <div>
            <label className={cn('block text-sm font-medium mb-1', text.primary)}>
              {t('startDate')}
            </label>
            <DatePicker
              selected={parseDateValue(filters.startDate)}
              onChange={(date) => handleChange('startDate', date ? formatDateISO(date) : '')}
              dateFormat={dateFormatForDisplay}
              locale={datepickerLocale.key}
              className={cn(
                'w-full p-2 border rounded-md',
                background.primary,
                text.primary,
                'border-gray-200 dark:border-gray-700',
                'focus:ring-2 focus:ring-blue-500'
              )}
              calendarClassName="booking-filters-datepicker"
              placeholderText={t('selectDate')}
              isClearable
              todayButton={t('today')}
            />
          </div>
          <div>
            <label className={cn('block text-sm font-medium mb-1', text.primary)}>
              {t('endDate')}
            </label>
            <DatePicker
              selected={parseDateValue(filters.endDate)}
              onChange={(date) => handleChange('endDate', date ? formatDateISO(date) : '')}
              dateFormat={dateFormatForDisplay}
              locale={datepickerLocale.key}
              className={cn(
                'w-full p-2 border rounded-md',
                background.primary,
                text.primary,
                'border-gray-200 dark:border-gray-700',
                'focus:ring-2 focus:ring-blue-500'
              )}
              calendarClassName="booking-filters-datepicker"
              placeholderText={t('selectDate')}
              isClearable
              todayButton={t('today')}
              minDate={parseDateValue(filters.startDate)}
            />
          </div>
        </div>
      )}

      {/* Custom Time Range Inputs - Only show when custom is selected */}
      {timePreset === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <div>
            <label className={cn('block text-sm font-medium mb-1', text.primary)}>
              {t('startTime')}
            </label>
            <input
              type="time"
              value={filters.startTime || ''}
              onChange={(e) => handleChange('startTime', e.target.value)}
              className={cn(
                'w-full p-2 border rounded-md',
                background.primary,
                text.primary,
                'border-gray-200 dark:border-gray-700',
                'focus:ring-2 focus:ring-blue-500'
              )}
            />
          </div>
          <div>
            <label className={cn('block text-sm font-medium mb-1', text.primary)}>
              {t('endTime')}
            </label>
            <input
              type="time"
              value={filters.endTime || ''}
              onChange={(e) => handleChange('endTime', e.target.value)}
              className={cn(
                'w-full p-2 border rounded-md',
                background.primary,
                text.primary,
                'border-gray-200 dark:border-gray-700',
                'focus:ring-2 focus:ring-blue-500'
              )}
            />
          </div>
        </div>
      )}

          {/* Clear Filters Button - Shows when filters differ from smart defaults */}
          {(filters.status !== 'pending,confirmed,waitlist' || filters.routeId || datePreset !== 'recentAndUpcoming' || timePreset !== 'all' || filters.search) && (
            <button
              onClick={() => {
                // Reset to smart defaults, not empty filters
                setDatePreset('recentAndUpcoming');
                setTimePreset('all');
                updateFilters(() => ({
                  status: 'all',
                  routeId: '',
                  startDate: '', // Will be set by datePreset useEffect
                  endDate: '',   // Will be set by datePreset useEffect
                  startTime: '',
                  endTime: '',
                  search: ''
                }));
              }}
              className={cn(
                'mt-4 px-4 py-2 text-sm rounded-md',
                'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
                text.primary
              )}
            >
              {t('resetFilters')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingFilters;
